export const categoryDetailsEN = {
  brakes: {
    title: 'Brake Components for All Vehicles',
    intro: 'High-quality brake discs, brake pads and complete brake systems for maximum safety.',
    content: `
      <h3>Why are high-quality brakes so important?</h3>
      <p>The brake system is the most important safety element of your vehicle. Worn or inferior brake components can lead to dangerous situations. At Autoteile Flott, we only offer tested brand quality that meets the highest safety standards.</p>
      
      <h3>Our Brake Components Overview</h3>
      <ul>
        <li><strong>Brake Discs:</strong> Drilled and slotted versions for optimal braking performance</li>
        <li><strong>Brake Pads:</strong> Organic and metallic variants for different driving styles</li>
        <li><strong>Brake Fluid:</strong> DOT 3, DOT 4 and DOT 5.1 for all requirements</li>
        <li><strong>Brake Calipers:</strong> Original and high-quality alternatives</li>
        <li><strong>Brake Hoses:</strong> Reinforced versions for longevity</li>
      </ul>
      
      <h3>Frequently Asked Questions</h3>
      <p><strong>How often should brake pads be replaced?</strong><br>
      Usually every 30,000 to 70,000 km, depending on your driving style and driving conditions.</p>
      
      <p><strong>How do I recognize worn brake discs?</strong><br>
      Squeaking noises when braking, vibrations in the steering wheel, or extended braking distances are clear signs.</p>
      
      <p><strong>Expert Advice:</strong> Call us and we'll find the right brake components for your vehicle – guaranteed compatible and at the best price-performance ratio.</p>
    `,
    keywords: ['Brake Discs', 'Brake Pads', 'Brake Fluid', 'Brake Calipers', 'Brake Hoses']
  },
  filters: {
    title: 'Filters for Optimal Engine Performance',
    intro: 'Air filters, oil filters, fuel filters – for clean combustion and long engine life.',
    content: `
      <h3>The Importance of Regular Filter Changes</h3>
      <p>Filters are the "kidneys" of your vehicle. They keep dirt, dust and contaminants away and ensure that the engine and fuel system work properly. Timely replacement saves fuel and prevents expensive repairs.</p>
      
      <h3>Our Filter Range</h3>
      <ul>
        <li><strong>Air Filters:</strong> Standard and sport air filters for better performance</li>
        <li><strong>Oil Filters:</strong> For all engine types, including synthetic oils</li>
        <li><strong>Fuel Filters:</strong> Gasoline and diesel, also for modern direct injection engines</li>
        <li><strong>Cabin Filters:</strong> With and without activated carbon for clean air</li>
        <li><strong>Transmission Oil Filters:</strong> For automatic transmissions</li>
      </ul>
      
      <h3>Frequently Asked Questions</h3>
      <p><strong>How often should the air filter be replaced?</strong><br>
      Usually every 15,000 to 30,000 km or once a year.</p>
      
      <p><strong>What happens with a clogged fuel filter?</strong><br>
      The engine can jerk, lose power, or in the worst case not start at all.</p>
      
      <p><strong>Benefit from our expertise:</strong> We advise you which filters are optimal for your maintenance interval.</p>
    `,
    keywords: ['Air Filter', 'Oil Filter', 'Fuel Filter', 'Cabin Filter', 'Transmission Oil Filter']
  },
  chassis: {
    title: 'Chassis Components for Safe Driving',
    intro: 'Shock absorbers, springs, control arms – for comfort and driving safety.',
    content: `
      <h3>An intact chassis is essential</h3>
      <p>The chassis connects your vehicle to the road. Worn components affect not only driving comfort but also safety. We supply high-quality chassis parts that fit your vehicle perfectly.</p>
      
      <h3>Our Chassis Components</h3>
      <ul>
        <li><strong>Shock Absorbers:</strong> Gas and oil dampers for all vehicle types</li>
        <li><strong>Springs:</strong> Original springs and sport springs</li>
        <li><strong>Control Arms:</strong> Complete with bushings and ball joints</li>
        <li><strong>Stabilizers:</strong> For better cornering behavior</li>
        <li><strong>Stub Axles and Wheel Bearings:</strong> In OEM quality</li>
      </ul>
      
      <h3>Frequently Asked Questions</h3>
      <p><strong>When do shock absorbers need to be replaced?</strong><br>
      When handling becomes spongy, braking distances increase, or tire wear is uneven.</p>
      
      <p><strong>Can I install sport springs without problems?</strong><br>
      It depends on the vehicle. We'll be happy to advise you on the options and registration.</p>
      
      <p><strong>Trust our experience:</strong> We find the right chassis parts for your requirements.</p>
    `,
    keywords: ['Shock Absorbers', 'Springs', 'Control Arms', 'Stabilizers', 'Wheel Bearings']
  },
  electrical: {
    title: 'Electrical Components for Reliability',
    intro: 'Batteries, starters, alternators – so your vehicle always starts.',
    content: `
      <h3>Electrical Systems – the heart of modern vehicles</h3>
      <p>Without functioning electrics, nothing works. From the battery to the starter to the alternator – we offer reliable components for all electrical systems of your vehicle.</p>
      
      <h3>Our Electrical Range</h3>
      <ul>
        <li><strong>Starter Batteries:</strong> AGM, EFB and lead-acid batteries</li>
        <li><strong>Starters:</strong> For gasoline and diesel engines</li>
        <li><strong>Alternators:</strong> Original and reconditioned quality</li>
        <li><strong>Spark Plugs:</strong> Copper, platinum and iridium</li>
        <li><strong>Sensors:</strong> Lambda sensors, ABS sensors, crankshaft sensors, camshaft sensor, exhaust gas temperature sensor, differential pressure sensor, NOX sensor, oil pressure sensor, coolant temperature sensor, boost pressure sensor, intake manifold pressure sensor</li>
      </ul>
      
      <h3>Frequently Asked Questions</h3>
      <p><strong>How long does a car battery last?</strong><br>
      Usually 4-6 years, depending on usage and weather conditions.</p>
      
      <p><strong>What are signs of a defective starter?</strong><br>
      Clicking noises when attempting to start or a very slowly turning flywheel.</p>
      
      <p><strong>Get advice:</strong> We check which electrical components your vehicle needs.</p>
    `,
    keywords: ['Battery', 'Starter', 'Alternator', 'Spark Plugs', 'Sensors']
  },
  lighting: {
    title: 'Lighting for Safety and Visibility',
    intro: 'Headlights, taillights, LED technology – for optimal visibility day and night.',
    content: `
      <h3>See and be seen</h3>
      <p>Functioning lighting is legally required and vital. We carry all types of bulbs and lighting components for your vehicle.</p>
      
      <h3>Our Lighting Range</h3>
      <ul>
        <li><strong>Headlights:</strong> Halogen, xenon and LED headlights</li>
        <li><strong>Bulbs:</strong> H1, H4, H7 and all common types</li>
        <li><strong>Taillights:</strong> Original and tuning variants</li>
        <li><strong>Fog Lights:</strong> For better visibility in bad weather</li>
        <li><strong>Interior Lighting:</strong> LED conversion kits</li>
      </ul>
      
      <h3>Frequently Asked Questions</h3>
      <p><strong>Can I install LED bulbs in halogen headlights?</strong><br>
      Only with appropriate approval and test mark, otherwise the operating license expires.</p>
      
      <p><strong>How long do xenon bulbs last?</strong><br>
      About 2,000 to 3,000 operating hours, significantly longer than halogen lamps.</p>
      
      <p><strong>We're happy to advise you:</strong> Find the right lighting for your vehicle – legal and powerful.</p>
    `,
    keywords: ['Headlights', 'Bulbs', 'Taillights', 'LED', 'Xenon']
  },
  exhaust: {
    title: 'Exhaust Systems for Performance and Environment',
    intro: 'Mufflers, catalytic converters, exhaust pipes – for clean emissions and optimal performance.',
    content: `
      <h3>The exhaust system – more than just noise reduction</h3>
      <p>An intact exhaust system not only reduces noise but also pollutants and influences engine performance. We offer complete systems and individual components in the best quality.</p>
      
      <h3>Our Exhaust Range</h3>
      <ul>
        <li><strong>Rear Silencers:</strong> Original and sport exhaust</li>
        <li><strong>Catalytic Converters:</strong> Metal catalysts and ceramic cats</li>
        <li><strong>Center Silencers:</strong> For all vehicle types</li>
        <li><strong>Particulate Filters:</strong> For diesel vehicles</li>
        <li><strong>Exhaust Pipes and Clamps:</strong> Stainless quality</li>
      </ul>
      
      <h3>Frequently Asked Questions</h3>
      <p><strong>How do I recognize a defective catalytic converter?</strong><br>
      Loss of power, increased fuel consumption and an illuminated engine control light.</p>
      
      <p><strong>Can I have a sport exhaust registered?</strong><br>
      Yes, if it has an ABE or parts certificate.</p>
      
      <p><strong>Call us:</strong> We find the right exhaust system for your vehicle.</p>
    `,
    keywords: ['Rear Silencer', 'Catalytic Converter', 'Particulate Filter', 'Exhaust Pipe', 'Sport Exhaust']
  },
  clutch: {
    title: 'Clutches for Smooth Gear Changes',
    intro: 'Clutch kits, release bearings, flywheels – for perfect shifting.',
    content: `
      <h3>The clutch – the link between engine and transmission</h3>
      <p>A worn clutch becomes noticeable through slipping, difficult shifting or noises. We supply complete clutch kits and individual components for all vehicles.</p>
      
      <h3>Our Clutch Range</h3>
      <ul>
        <li><strong>Clutch Kits:</strong> 3-piece with pressure plate, disc and release bearing</li>
        <li><strong>Dual-Mass Flywheels:</strong> Original and reinforced versions</li>
        <li><strong>Release Systems:</strong> Hydraulic and mechanical</li>
        <li><strong>Clutch Discs:</strong> Standard and for high torques</li>
        <li><strong>Master Cylinders:</strong> For hydraulic clutches</li>
      </ul>
      
      <h3>Frequently Asked Questions</h3>
      <p><strong>How long does a clutch last?</strong><br>
      With normal driving between 100,000 and 200,000 km.</p>
      
      <p><strong>How much does a clutch replacement cost?</strong><br>
      It depends on the vehicle. We'll be happy to advise you on the costs for your model.</p>
      
      <p><strong>Trust our advice:</strong> We find the right clutch kit for your vehicle.</p>
    `,
    keywords: ['Clutch Kit', 'Dual-Mass Flywheel', 'Release Bearing', 'Clutch Disc', 'Central Release Bearing', 'Master Cylinder', 'Slave Cylinder']
  },
  engine: {
    title: 'Engine Components for Performance and Reliability',
    intro: 'Cylinder head gaskets, timing chains, water pumps – for a healthy engine.',
    content: `
      <h3>The engine – the heart of your vehicle</h3>
      <p>Engine components must withstand the highest loads. We offer spare parts in OEM quality that guarantee longevity and performance.</p>
      
      <h3>Our Engine Range</h3>
      <ul>
        <li><strong>Cylinder Head Gaskets:</strong> For all engine types</li>
        <li><strong>Timing Chains and Belts:</strong> In OEM quality, or cheaper from brand manufacturers for cost-effective repair</li>
        <li><strong>Water Pumps:</strong> Mechanical, switchable or purely electric water pumps</li>
        <li><strong>Pistons and Connecting Rods:</strong> Standard and reinforced versions</li>
        <li><strong>Camshafts:</strong> Original and performance variants</li>
        <li><strong>Additives:</strong> Hydraulic lifter additive, injection system cleaner, lead substitute for classic car engines, visco additive against oil consumption</li>
        <li><strong>Valves:</strong> Inlet and exhaust valves, valve stem seals in OEM quality</li>
        <li><strong>Oil Pan:</strong> Steel pans rust over time, a common problem during MOT inspections</li>
        <li><strong>Sealing Rings:</strong> e.g.: Crankshaft seals often cause oil loss and also a common problem during MOT inspections</li>
        <li><strong>Thermostat:</strong> ensures optimal cooling for the engine and also a warm interior in winter</li>
      </ul>
      
      <h3>Frequently Asked Questions</h3>
      <p><strong>When does the timing belt need to be replaced?</strong><br>
      According to manufacturer specifications, usually between 60,000 and 220,000 km.</p>
      
      <p><strong>What happens with a defective cylinder head gasket?</strong><br>
      Coolant loss, white smoke from the exhaust and overheating are typical symptoms.</p>
      
      <p><strong>Get advice:</strong> We find the right engine components for your vehicle.</p>
    `,
    keywords: ['Cylinder Head Gasket', 'Timing Belt', 'Timing Chain', 'Water Pump', 'Pistons', 'Sealing Ring', 'Valve', 'Thermostat', 'Additive', 'Oil Consumption']
  },
  body: {
    title: 'Body Parts for Appearance and Function',
    intro: 'Mirrors, door handles, bumpers – for a perfect appearance.',
    content: `
      <h3>Body parts – for form and function</h3>
      <p>Damaged or worn body parts affect not only appearance but also functionality. We supply matching spare parts for all vehicle models.</p>
      
      <h3>Our Body Range</h3>
      <ul>
        <li><strong>Side Mirrors:</strong> Complete or individual mirror glasses</li>
        <li><strong>Mirror Glass:</strong> Replacement glasses for various vehicle models</li>
        <li><strong>Door Handles:</strong> Interior and exterior</li>
        <li><strong>Bumpers:</strong> Primed or painted</li>
        <li><strong>Fenders:</strong> Original and aftermarket</li>
        <li><strong>Sills / Entry:</strong> Repair panels and spare parts</li>
        <li><strong>Repair Panel for Fenders:</strong> For precise body repairs</li>
        <li><strong>Trim Strips:</strong> Chrome and plastic</li>
      </ul>
      
      <h3>Frequently Asked Questions</h3>
      <p><strong>Can I paint body parts myself?</strong><br>
      With appropriate preparation yes, but for best results we recommend a specialist workshop.</p>
      
      <p><strong>Are aftermarket parts as good as original parts?</strong><br>
      High-quality aftermarket parts often offer the same quality level at a lower price.</p>
      
      <p><strong>We're happy to advise you:</strong> Find the right body parts for your vehicle.</p>
    `,
    keywords: ['Side Mirrors', 'Door Handles', 'Bumpers', 'Fenders', 'Trim Strips', 'Mirror Glass', 'Sills', 'Repair Panel']
  },
  drivetrain: {
    title: 'Drivetrain Components for Powerful Power Transmission',
    intro: 'Drive shafts, CV joints, propeller shafts – for safe power transmission to the wheels.',
    content: `
      <h3>The drivetrain – the heart of power transmission</h3>
      <p>The drivetrain transmits engine power to the wheels. Wear or defects lead to vibrations, noises and can impair driving safety. We supply high-quality drivetrain components in OEM quality.</p>
      
      <h3>Our Drivetrain Range</h3>
      <ul>
        <li><strong>Drive Shafts:</strong> Complete with joints and boots</li>
        <li><strong>CV Joints:</strong> For front, rear and all-wheel drive</li>
        <li><strong>Propeller Shafts:</strong> For rear-wheel and all-wheel drive vehicles</li>
        <li><strong>CV Joints:</strong> Constant velocity joints for smooth running</li>
        <li><strong>Drive Shaft Boots:</strong> For reliable protection of joints</li>
        <li><strong>Propeller Shaft Bearings:</strong> For vibration-free running</li>
      </ul>
      
      <h3>Frequently Asked Questions</h3>
      <p><strong>How do I recognize a defective drive shaft?</strong><br>
      Typical symptoms are clicking noises when steering, vibrations when accelerating or visibly damaged boots.</p>
      
      <p><strong>Can drive shafts be repaired?</strong><br>
      In most cases, replacement is more economical and safer than repair.</p>
      
      <p><strong>How often should drive shaft boots be checked?</strong><br>
      The boots should be checked for cracks and damage at every inspection.</p>
      
      <p><strong>Trust our experience:</strong> We find the right drivetrain components for your vehicle – whether front-wheel, rear-wheel or all-wheel drive.</p>
    `,
    keywords: ['Drive Shaft', 'CV Joint', 'Propeller Shaft', 'CV Joint', 'Constant Velocity', 'Boot']
  },
  climate: {
    title: 'Climate & Heating for Perfect Temperature',
    intro: 'AC compressors, heater fans, radiators – for comfortable climate in the vehicle.',
    content: `
      <h3>Climate and heating systems – comfort in every season</h3>
      <p>A functioning air conditioning and heating system ensure comfortable temperatures and clear vision. We supply all components for your climate system.</p>
      
      <h3>Our Climate Range</h3>
      <ul>
        <li><strong>AC Compressors:</strong> Original and high-quality alternatives</li>
        <li><strong>Condensers:</strong> For efficient cooling</li>
        <li><strong>Heater Fans:</strong> For warm air in winter</li>
        <li><strong>Heater Fan Resistor:</strong> when the fan speed settings no longer switch through</li>
        <li><strong>Coolant Radiators:</strong> For optimal engine temperature</li>
        <li><strong>Dryers and Filters:</strong> For clean air conditioning systems</li>
      </ul>
      
      <h3>Frequently Asked Questions</h3>
      <p><strong>Why doesn't my air conditioning cool properly anymore?</strong><br>
      Common causes are refrigerant loss, defective compressor or clogged condenser.</p>
      
      <p><strong>How often should the air conditioning be serviced?</strong><br>
      Once a year, ideally before summer.</p>
      
      <p><strong>Trust our expertise:</strong> We find the right components for your climate system.</p>
    `,
    keywords: ['AC Compressor', 'Condenser', 'Heater Fan', 'Radiator', 'Climate Service']
  },
  others: {
    title: 'Other Auto Parts and Accessories',
    intro: 'Everything else you need – from wipers to trailer hitches.',
    content: `
      <h3>Comprehensive range for all needs</h3>
      <p>In addition to the main categories, we carry a wide range of other auto parts and accessories. Whatever you're looking for – we have it or can get it for you.</p>
      
      <h3>Other Products</h3>
      <ul>
        <li><strong>Wiper Blades:</strong> All lengths and types</li>
        <li><strong>Tires:</strong> Summer, winter and all-season tires</li>
        <li><strong>Trailer Hitches:</strong> Fixed and removable</li>
        <li><strong>Seals:</strong> For doors, windows and trunk</li>
        <li><strong>Care Products:</strong> Engine oil, antifreeze, cleaners</li>
        <li><strong>Tire Pressure Sensor:</strong> For modern vehicles with TPMS</li>
        <li><strong>Polish:</strong> For shiny paint care</li>
        <li><strong>Headlight Sealant / Polish:</strong> For clear visibility and well-maintained appearance</li>
        <li><strong>Windshield Washer Fluid / Winter Antifreeze:</strong> For clear vision in freezing temperatures</li>
        <li><strong>Windshield Cover / Half Garage:</strong> Protection from weather</li>
      </ul>
      
      <h3>Frequently Asked Questions</h3>
      <p><strong>Do you also have special parts for classic cars?</strong><br>
      Yes, we can also source hard-to-find parts for classic cars.</p>
      
      <p><strong>Do you also offer installation service?</strong><br>
      We're happy to recommend partner workshops.</p>
      
      <p><strong>Contact us:</strong> For all auto parts you don't find online – we're here to help!</p>
    `,
    keywords: ['Wiper Blades', 'Tires', 'Trailer Hitch', 'Care Products', 'Accessories', 'Tire Pressure Sensor', 'Polish', 'Windshield Antifreeze']
  }
};
